import {AfterViewInit, Component, EventEmitter, Input, OnChanges, OnDestroy, Output, QueryList, SimpleChanges, ViewChild, ViewChildren} from '@angular/core';
import {of, combineLatest, Subject} from 'rxjs';
import {catchError, filter, finalize, takeUntil} from 'rxjs/operators';
import {CurrencyPipe, DatePipe, PercentPipe} from '@angular/common';
import {MatAccordion, MatExpansionPanel} from '@angular/material/expansion';
import {ProposalService} from '../../../services/proposal-service/proposal.service';
import {ProposalVersionHistoryEntry, VersionFieldChange} from '../../../model/proposal/proposal-version-history-entry';
import {TranslateService} from '@ngx-translate/core';
import {Proposal} from '../../../model/proposal/proposal';

interface ParsedChange {
  dto: string;
  entityId: string | null;
  property: string;
  oldValue: any;
  newValue: any;
}

interface DisplayRow {
  label: string;
  formattedNew: string;
  formattedOld: string;
  hasOld: boolean;
}

@Component({
    selector: 'app-proposal-version-history',
    templateUrl: './proposal-version-history.component.html',
    styleUrls: ['./proposal-version-history.component.scss'],
    standalone: false,
    providers: [DatePipe, CurrencyPipe, PercentPipe]
})
export class ProposalVersionHistoryComponent implements OnChanges, AfterViewInit, OnDestroy {

  @Input() proposalId: string;

  /** Reverse-ordered (newest first) entries with computed display version numbers and pre-rendered rows. */
  versions: Array<{ entry: ProposalVersionHistoryEntry; versionNumber: number; rows: DisplayRow[] }> = [];
  loading: boolean = false;
  errored: boolean = false;

  /**
   * Ids of attached files that still exist in the latest proposal version.
   * Used by the inner expandable to suppress download buttons for files that
   * have been deleted in the meantime (showing a "Deleted" badge instead).
   */
  currentFileIds: Set<string> = new Set<string>();

  @ViewChild(MatAccordion) accordion: MatAccordion;
  @ViewChildren(MatExpansionPanel) panels: QueryList<MatExpansionPanel>;

  private _allDetailsOpen = false;

  @Input() set allDetailsOpen(value: boolean) {
    if (value) {
      this.accordion?.openAll();
    } else {
      this.accordion?.closeAll();
    }
    this._allDetailsOpen = value;
  }

  get allDetailsOpen(): boolean {
    return this._allDetailsOpen;
  }

  @Output() allDetailsOpenChange = new EventEmitter<boolean>();

  private readonly destroy$ = new Subject<boolean>();

  constructor(private readonly proposalService: ProposalService,
              private readonly translate: TranslateService,
              private readonly datePipe: DatePipe,
              private readonly currencyPipe: CurrencyPipe,
              private readonly percentPipe: PercentPipe) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['proposalId'] && this.proposalId) {
      this.loadHistory();
    }
  }

  ngAfterViewInit(): void {
    this.panels.changes
      .pipe(
        filter((value: QueryList<MatExpansionPanel>) => value.length > 0),
        takeUntil(this.destroy$)
      )
      .subscribe((value: QueryList<MatExpansionPanel>) => {
        const obs = value.toArray().map(panel => panel.expandedChange);
        combineLatest(obs).pipe(takeUntil(this.destroy$)).subscribe((arr: boolean[]) => {
          if (arr.every(isOpen => isOpen)) {
            this._allDetailsOpen = true;
            this.allDetailsOpenChange.emit(true);
          } else if (arr.every(isOpen => !isOpen)) {
            this._allDetailsOpen = false;
            this.allDetailsOpenChange.emit(false);
          }
        });
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  private loadHistory(): void {
    this.loading = true;
    this.errored = false;
    this.versions = [];
    this.proposalService.getProposalHistory(this.proposalId).pipe(
      catchError(() => {
        this.errored = true;
        return of([] as ProposalVersionHistoryEntry[]);
      }),
      finalize(() => this.loading = false)
    ).subscribe(history => {
      // Backend returns oldest -> newest. Newest version gets number = total, creation gets number 1.
      this.versions = history
        .map((entry, idx) => ({
          entry,
          versionNumber: idx + 1,
          rows: this.buildDisplayRows(entry.changes ?? [], entry.proposal)
        }))
        .reverse();
      this.currentFileIds = this.computeCurrentFileIds();
    });
  }

  /** Builds the set of file ids present in the latest proposal version. */
  private computeCurrentFileIds(): Set<string> {
    const latest = this.versions[0];
    const files = latest?.entry?.proposal?.proposalInformation?.attachedFiles ?? [];
    return new Set(files.map(f => f?.id).filter((id): id is string => !!id));
  }

  /** Parses a JaVers field string into its DTO/entityId/property components. */
  private parseField(field: string, oldValue: any, newValue: any): ParsedChange | null {
    if (!field || typeof field !== 'string') {
      return null;
    }
    // Handles all shapes the backend emits, e.g.:
    //   "...PartnerDto/.<property>"           (collapsed swap)
    //   "...BinaryDataDto/<uuid>.attachedFiles"
    //   "de.ppi.hvb.tradingplatform.proposalfeature.dto.ProposalInformationDto/<uuid>.<property>"
    const match = /(?:\.\.\.|^|\.)([A-Z]\w+)\/([^.]*)\.(.+)$/.exec(field);
    if (!match) {
      return null;
    }
    const [, dto, entityIdRaw, property] = match;
    return {
      dto,
      entityId: entityIdRaw && entityIdRaw.length > 0 ? entityIdRaw : null,
      property,
      oldValue,
      newValue
    };
  }

  /**
   * Builds display-ready rows from the (already-filtered, already-collapsed) backend change list.
   */
  buildDisplayRows(rawChanges: VersionFieldChange[], proposal: Proposal): DisplayRow[] {
    if (!rawChanges || rawChanges.length === 0) {
      return [];
    }
    const rows: DisplayRow[] = [];
    for (const change of rawChanges) {
      const parsed = this.parseField(change.field, change.oldValue, change.newValue);
      if (!parsed) continue;
      rows.push(this.buildRow(parsed, proposal));
    }
    return rows;
  }

  private buildRow(parsed: ParsedChange, proposal: Proposal): DisplayRow {
    const formattedNew = this.formatValueForField(parsed.newValue, parsed.dto, parsed.property, parsed.entityId, proposal);
    const formattedOld = this.formatValueForField(parsed.oldValue, parsed.dto, parsed.property, parsed.entityId, proposal);
    const hasOld = parsed.oldValue != null && parsed.oldValue !== '';
    return {
      label: this.fieldLabel(parsed),
      formattedNew,
      formattedOld,
      hasOld
    };
  }

  private formatValueForField(value: any, dto: string, property: string, entityId: string | null, proposal: Proposal): string {
    if (value == null || value === '') return this.dashLabel();

    if (dto === 'ProposalInformationDto') {
      if (property === 'insuredSumType') {
        return this.translate.instant(`createProposal.housingInsuranceSumTypeOptions.${value}`);
      }
      if (property === 'coveragePercentageRequested') {
        return this.percentPipe.transform(value, '1.2-2') ?? String(value);
      }
      if (property === 'insuredAmount' || property === 'annualSalesAmount') {
        const currency = proposal?.proposalInformation?.insuredAmountCurrency;
        return this.currencyPipe.transform(value, currency) ?? String(value);
      }
    }

    if (dto === 'RiskLocationDto') {
      if (['skenTotalValueEur', 'skenRegionalizedTotalValueEur', 'skenRegionalizedTotalValueM', 'skenBuildingValue14'].includes(property)) {
        return this.currencyPipe.transform(value, 'EUR') ?? String(value);
      }
    }

    if (dto === 'PreviousDamageDto' && property === 'amountPaid') {
      const damage = proposal?.previousDamages?.find(d => d.id === entityId);
      const currency = damage?.currency ?? 'EUR';
      return this.currencyPipe.transform(value, currency) ?? String(value);
    }

    return this.formatValue(value);
  }

  private fieldLabel(parsed: ParsedChange): string {
    return this.translate.instant(`proposalDetails.versionHistoryTab.fields.${parsed.dto}.${parsed.property}`);
  }

  private dashLabel(): string {
    return '—';
  }

  /** Type-aware value formatting for booleans, dates, arrays, primitives. */
  private formatValue(value: any): string {
    if (value == null || value === '') {
      return this.dashLabel();
    }
    if (typeof value === 'boolean') {
      return this.translate.instant(value ? 'proposalDetails.yes' : 'proposalDetails.no');
    }
    if (Array.isArray(value)) {
      if (value.length === 0) return this.dashLabel();
      if (value.every(v => typeof v === 'string' || typeof v === 'number')) {
        return value.join(', ');
      }
      return `${value.length}`;
    }
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
      return this.datePipe.transform(value, 'mediumDate') ?? value;
    }
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value);
      } catch {
        return String(value);
      }
    }
    return String(value);
  }

  trackByVersion(_index: number, item: { versionNumber: number }): number {
    return item.versionNumber;
  }

  trackByRow(index: number, _row: DisplayRow): number {
    return index;
  }
}
